import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import Select from 'react-select';
import SalesLayout from '../../components/SalesLayout';
import { PageHero, HeroButton, HeroIcons } from '../../components/SalesUI';
import PageLoader from '../../components/PageLoader';
import axiosInstance from '../../utils/axiosInstance';

const API_BASE_PATH = '/admin/sales-my-work-tasks';

const DEFAULT_TASK_CATEGORIES = [
    { name: 'New inquiries (calls)', color: '#0EA5E9' },
    { name: 'New inquiries (walk-in)', color: '#6366F1' },
    { name: 'Follow-up call', color: '#22C55E' },
    { name: 'WhatsApp follow-up', color: '#10B981' },
    { name: 'Email follow-up', color: '#F97316' },
    { name: 'Campus visit / counseling', color: '#A855F7' },
    { name: 'Parent meeting', color: '#EC4899' },
    { name: 'Registration completion', color: '#F59E0B' },
    { name: 'Document collection / verification', color: '#6B7280' },
    { name: 'Internal coordination (academic/admin)', color: '#14B8A6' },
    { name: 'Document uploading', color: '#0F766E' },
    { name: 'Meeting', color: '#7C3AED' },
    { name: 'Seminar', color: '#EF4444' },
    { name: 'School visit', color: '#3B82F6' },
    { name: 'Leaflet distribution', color: '#FACC15' },
    { name: 'Other', color: '#9CA3AF' },
];

export default function MyWork({ pageTitle, section, taskCategories = [] }) {
    const [tasks, setTasks] = useState([]);
    const [stats, setStats] = useState({
        total_tasks: 0,
        total_hours: 0,
        avg_hours_per_task: 0,
        tasks_this_month: 0,
        hours_this_month: 0,
    });
    const [monthlyStats, setMonthlyStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [formData, setFormData] = useState({
        subject: '',
        description: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '',
        end_time: '',
    });

    const effectiveCategories = taskCategories.length ? taskCategories : DEFAULT_TASK_CATEGORIES;

    const categoryOptions = useMemo(
        () =>
            effectiveCategories.map((cat) => ({
                value: cat.name,
                label: cat.name,
                color: cat.color,
            })),
        [effectiveCategories],
    );

    const filterCategoryOptions = useMemo(
        () => [
            { value: '', label: 'All Categories', color: '#64748B' },
            ...categoryOptions,
        ],
        [categoryOptions],
    );

    const selectStyles = useMemo(
        () => ({
            control: (base, state) => ({
                ...base,
                borderColor: state.isFocused ? '#6366F1' : '#D1D5DB',
                boxShadow: 'none',
                minHeight: '2.5rem',
                '&:hover': {
                    borderColor: '#6366F1',
                },
            }),
            option: (base, state) => {
                const optionColor = state.data.color;
                return {
                    ...base,
                    backgroundColor: state.isSelected
                        ? optionColor
                        : state.isFocused
                            ? '#EEF2FF'
                            : base.backgroundColor,
                    color: state.isSelected ? '#FFFFFF' : '#1F2937',
                };
            },
            menuPortal: (base) => ({ ...base, zIndex: 50 }),
        }),
        [],
    );

    useEffect(() => {
        fetchTasks();
        fetchMonthlyStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, startDate, endDate]);

    const fetchTasks = async () => {
        try {
            setLoading(true);
            const params = {};
            if (selectedCategory) params.category = selectedCategory;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const response = await axiosInstance.get(API_BASE_PATH, { params });
            const data = response.data || {};

            if (data.success) {
                setTasks(Array.isArray(data.tasks) ? data.tasks : []);
                setStats(
                    data.stats || {
                        total_tasks: 0,
                        total_hours: 0,
                        avg_hours_per_task: 0,
                        tasks_this_month: 0,
                        hours_this_month: 0,
                    },
                );
            } else {
                setTasks([]);
            }
        } catch (error) {
            console.error('Error fetching tasks:', error);
            Swal.fire('Error', 'Failed to fetch work items', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchMonthlyStats = async () => {
        try {
            const year = new Date().getFullYear();
            const response = await axiosInstance.get(`${API_BASE_PATH}/monthly-stats`, { params: { year } });
            const data = response.data || {};

            if (data.success && Array.isArray(data.monthly_stats)) {
                setMonthlyStats(data.monthly_stats);
            } else {
                setMonthlyStats([]);
            }
        } catch (error) {
            console.error('Error fetching monthly stats:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.category) {
            Swal.fire('Validation', 'Please select a category', 'warning');
            return;
        }

        try {
            const url = editingTask ? `${API_BASE_PATH}/${editingTask.id}` : API_BASE_PATH;
            const method = editingTask ? 'put' : 'post';

            const response = await axiosInstance[method](url, formData);
            const data = response.data || {};

            if (data.success) {
                Swal.fire('Success', data.message, 'success');
                setShowForm(false);
                setEditingTask(null);
                resetForm();
                fetchTasks();
                fetchMonthlyStats();
            } else {
                Swal.fire('Error', data.message || 'Failed to save work item', 'error');
            }
        } catch (error) {
            console.error('Error saving task:', error);
            Swal.fire('Error', 'Failed to save work item', 'error');
        }
    };

    const handleEdit = (task) => {
        setEditingTask(task);

        let formattedDate = '';
        if (task.date) {
            try {
                const d = new Date(task.date);
                if (!Number.isNaN(d.getTime())) {
                    formattedDate = d.toISOString().split('T')[0];
                }
            } catch (_) {
                formattedDate = '';
            }
        }

        setFormData({
            subject: task.subject,
            description: task.description || '',
            category: task.category,
            date: formattedDate || new Date().toISOString().split('T')[0],
            start_time: task.start_time,
            end_time: task.end_time,
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#EF4444',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Yes, delete it!',
        });

        if (result.isConfirmed) {
            try {
                const response = await axiosInstance.delete(`${API_BASE_PATH}/${id}`);
                const data = response.data || {};

                if (data.success) {
                    Swal.fire('Deleted!', data.message, 'success');
                    fetchTasks();
                    fetchMonthlyStats();
                } else {
                    Swal.fire('Error', data.message || 'Failed to delete work item', 'error');
                }
            } catch (error) {
                console.error('Error deleting task:', error);
                Swal.fire('Error', 'Failed to delete work item', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            subject: '',
            description: '',
            category: '',
            date: new Date().toISOString().split('T')[0],
            start_time: '',
            end_time: '',
        });
    };

    const getCategoryColor = (category) => {
        const cat = effectiveCategories.find((c) => c.name === category);
        return cat?.color || '#0EA5E9';
    };

    const categoryData = effectiveCategories
        .map((cat) => {
            const categoryTasks = tasks.filter((t) => t.category === cat.name);
            const totalHours = categoryTasks.reduce((sum, t) => sum + parseFloat(t.duration.toString()), 0);
            return {
                name: cat.name,
                tasks: categoryTasks.length,
                hours: parseFloat(totalHours.toFixed(2)),
                color: cat.color,
            };
        })
        .filter((d) => d.tasks > 0);

    return (
        <SalesLayout title={pageTitle} currentSection={section}>
            <div className="sales-stagger mx-auto max-w-7xl space-y-6">
                <PageHero
                    icon={HeroIcons.work}
                    title={pageTitle || 'My work'}
                    subtitle="Track daily outreach, follow-ups, and closing work to keep your pipeline healthy."
                    gradient="bg-gradient-to-br from-sky-600 via-indigo-600 to-violet-600"
                    actions={(
                        <HeroButton
                            type="button"
                            onClick={() => {
                                setShowForm(true);
                                setEditingTask(null);
                                resetForm();
                            }}
                        >
                            {HeroIcons.plus}
                            Add Work Item
                        </HeroButton>
                    )}
                />

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
                    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                        <div className="p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-blue-100">Total Actions</p>
                                    <p className="mt-2 text-3xl font-bold">{stats.total_tasks}</p>
                                </div>
                                <div className="rounded-full bg-white/20 p-3">
                                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-green-600 shadow-lg">
                        <div className="p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-green-100">Total Hours</p>
                                    <p className="mt-2 text-3xl font-bold">{stats.total_hours.toFixed(1)}</p>
                                </div>
                                <div className="rounded-full bg-white/20 p-3">
                                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg">
                        <div className="p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-purple-100">Avg Hours/Action</p>
                                    <p className="mt-2 text-3xl font-bold">{stats.avg_hours_per_task.toFixed(1)}</p>
                                </div>
                                <div className="rounded-full bg-white/20 p-3">
                                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg">
                        <div className="p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-orange-100">This Month</p>
                                    <p className="mt-2 text-3xl font-bold">{stats.tasks_this_month}</p>
                                </div>
                                <div className="rounded-full bg-white/20 p-3">
                                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 shadow-lg">
                        <div className="p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-pink-100">Month Hours</p>
                                    <p className="mt-2 text-3xl font-bold">{stats.hours_this_month.toFixed(1)}</p>
                                </div>
                                <div className="rounded-full bg-white/20 p-3">
                                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                    <div className="rounded-xl bg-white p-6 shadow-md">
                        <h2 className="mb-4 text-lg font-bold text-gray-900">Monthly Trend</h2>
                        {monthlyStats.length === 0 || monthlyStats.every((m) => m.task_count === 0) ? (
                            <div className="flex h-[300px] items-center justify-center text-gray-500">
                                <div className="text-center">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                    </svg>
                                    <p className="mt-2">No monthly data yet</p>
                                    <p className="text-sm">Log your actions to see the momentum build.</p>
                                </div>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={monthlyStats}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                    <XAxis dataKey="month" stroke="#6B7280" />
                                    <YAxis yAxisId="left" stroke="#8B5CF6" label={{ value: 'Actions', angle: -90, position: 'insideLeft' }} />
                                    <YAxis yAxisId="right" orientation="right" stroke="#10B981" label={{ value: 'Hours', angle: 90, position: 'insideRight' }} />
                                    <Tooltip contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }} />
                                    <Legend />
                                    <Line
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="task_count"
                                        stroke="#8B5CF6"
                                        strokeWidth={3}
                                        name="Actions"
                                        dot={{ fill: '#8B5CF6', r: 4 }}
                                        activeDot={{ r: 6 }}
                                    />
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="total_hours"
                                        stroke="#10B981"
                                        strokeWidth={3}
                                        name="Hours"
                                        dot={{ fill: '#10B981', r: 4 }}
                                        activeDot={{ r: 6 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        <div className="rounded-xl bg-white p-6 shadow-md">
                            <h2 className="mb-4 text-lg font-bold text-gray-900">Actions & Hours by Category</h2>
                            {categoryData.length === 0 ? (
                                <div className="flex h-[300px] items-center justify-center text-gray-500">
                                    <div className="text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        <p className="mt-2">No data available</p>
                                        <p className="text-sm">Add work items to see the distribution.</p>
                                    </div>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={categoryData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                        <XAxis dataKey="name" stroke="#6B7280" angle={-45} textAnchor="end" height={80} />
                                        <YAxis stroke="#6B7280" />
                                        <Tooltip contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }} />
                                        <Legend />
                                        <Bar dataKey="tasks" fill="#8B5CF6" name="Actions" radius={[8, 8, 0, 0]} />
                                        <Bar dataKey="hours" fill="#10B981" name="Hours" radius={[8, 8, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        <div className="rounded-xl bg-white p-6 shadow-md">
                            <h2 className="mb-4 text-lg font-bold text-gray-900">Hours by Category</h2>
                            {categoryData.length === 0 ? (
                                <div className="flex h-[300px] items-center justify-center text-gray-500">
                                    <div className="text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        <p className="mt-2">No data available</p>
                                        <p className="text-sm">Add work items to see the distribution.</p>
                                    </div>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={categoryData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine
                                            label={({ name, hours, percent }) => `${name}: ${hours}h (${(percent * 100).toFixed(0)}%)`}
                                            outerRadius={90}
                                            fill="#8884d8"
                                            dataKey="hours"
                                        >
                                            {categoryData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value, name) => [`${value.toFixed(2)} hours`, name]} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>

                <div className="rounded-xl bg-white p-6 shadow-md">
                    <h2 className="mb-4 text-lg font-bold text-gray-900">Filters</h2>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                            <Select
                                isClearable
                                placeholder="All Categories"
                                options={filterCategoryOptions}
                                value={filterCategoryOptions.find((option) => option.value === selectedCategory) ?? null}
                                onChange={(option) => setSelectedCategory(option?.value ?? '')}
                                styles={selectStyles}
                                classNamePrefix="react-select"
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={() => {
                                    setSelectedCategory('');
                                    setStartDate('');
                                    setEndDate('');
                                }}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl bg-white shadow-md">
                    <div className="border-b border-gray-200 px-6 py-4">
                        <h2 className="text-lg font-bold text-gray-900">Work Item List</h2>
                    </div>
                    <div className="p-6">
                        {loading ? (
                            <PageLoader label="Loading work items…" />
                        ) : tasks.length === 0 ? (
                            <div className="py-12 text-center text-gray-500">
                                No work items found. Click "Add Work Item" to get started!
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {tasks.map((task) => (
                                    <div
                                        key={task.id}
                                        className="flex items-start justify-between rounded-lg border border-gray-200 p-4 transition-shadow hover:shadow-md"
                                        style={{ borderLeftWidth: '4px', borderLeftColor: getCategoryColor(task.category) }}
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3">
                                                <h3 className="text-lg font-semibold text-gray-900">{task.subject}</h3>
                                                <span
                                                    className="rounded-full px-3 py-1 text-xs font-medium text-white"
                                                    style={{ backgroundColor: getCategoryColor(task.category) }}
                                                >
                                                    {task.category}
                                                </span>
                                            </div>
                                            {task.description && <p className="mt-1 text-sm text-gray-600">{task.description}</p>}
                                            <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-500">
                                                <span className="flex items-center">
                                                    <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                    {new Date(task.date).toLocaleDateString()}
                                                </span>
                                                <span className="flex items-center">
                                                    <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                    {task.start_time} - {task.end_time}
                                                </span>
                                                <span className="flex items-center font-semibold text-indigo-600">
                                                    <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                                                        />
                                                    </svg>
                                                    {parseFloat(task.duration.toString()).toFixed(2)} hours
                                                </span>
                                            </div>
                                        </div>
                                        <div className="ml-4 flex gap-2">
                                            <button
                                                onClick={() => handleEdit(task)}
                                                className="rounded-lg bg-indigo-50 p-2 text-indigo-600 transition-colors hover:bg-indigo-100"
                                            >
                                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                    />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(task.id)}
                                                className="rounded-lg bg-red-50 p-2 text-red-600 transition-colors hover:bg-red-100"
                                            >
                                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-900">{editingTask ? 'Edit Work Item' : 'Add Work Item'}</h2>
                            <button
                                onClick={() => {
                                    setShowForm(false);
                                    setEditingTask(null);
                                    resetForm();
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700">Subject *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.subject}
                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        placeholder="e.g., Close registration with Ramesh"
                                    />
                                </div>

                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        rows={3}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        placeholder="Add context, commitments, or next steps..."
                                    />
                                </div>

                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700">Category *</label>
                                    <Select
                                        isClearable
                                        placeholder="Select a category"
                                        options={categoryOptions}
                                        value={categoryOptions.find((option) => option.value === formData.category) ?? null}
                                        onChange={(option) => setFormData({ ...formData, category: option?.value ?? '' })}
                                        styles={selectStyles}
                                        classNamePrefix="react-select"
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                                    />
                                </div>

                                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">Date *</label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.date}
                                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">Start Time *</label>
                                        <input
                                            type="time"
                                            required
                                            value={formData.start_time}
                                            onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">End Time *</label>
                                        <input
                                            type="time"
                                            required
                                            value={formData.end_time}
                                            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingTask(null);
                                        resetForm();
                                    }}
                                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-colors hover:bg-indigo-700"
                                >
                                    {editingTask ? 'Update Work Item' : 'Create Work Item'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </SalesLayout>
    );
}
